'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';

const PRICE_MAX = 3000;
const PRICE_STEP = 50;

const COLORS: [string, string, 0 | 1][] = [
  ['černá', '#171413', 0], ['bílá', '#f3f0ea', 1], ['červená', '#b23b3b', 0], ['modrá', '#3a5a8a', 0],
  ['zelená', '#4a6b52', 0], ['žlutá', '#d9b84a', 1], ['růžová', '#d089a1', 1], ['fialová', '#7a5a8a', 0],
  ['oranžová', '#cc7a44', 0], ['šedá', '#9a958d', 1], ['béžová', '#cdbfa6', 1], ['vínová', '#6e2b3e', 0],
  ['hnědá', '#7a5a44', 0],
];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

const TYPES: [string, string][] = [
  ['Noční košilky', 'noční košilka'],
  ['Župany', 'župan'],
  ['Pyžama', 'pyžamo'],
  ['Topv & Tílka', 'top'],
  ['Setv & Soupravy', 'set'],
  ['Kalhotky', 'kalhotky'],
  ['Podprsenky', 'podprsenka'],
  ['Šortky', 'šortky'],
  ['Ostatní', 'ostatní'],
];

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export default function FilterSidebar({
  q, size, type, color, brand, brandsList,
  availability, priceMin, priceMax,
}: {
  q: string; size: string; type: string;
  color: string; brand: string; brandsList: string[];
  availability: string; priceMin: string; priceMax: string;
}) {
  const router = useRouter();

  const [s, setS] = useState({
    q,
    color,
    size,
    type,
    brand,
    stock: availability === 'skladem',
    pmin: priceMin ? parseInt(priceMin) : 0,
    pmax: priceMax ? parseInt(priceMax) : PRICE_MAX,
  });

  const set = (patch: Partial<typeof s>) => setS(prev => ({ ...prev, ...patch }));

  const buildUrl = (st: typeof s) => {
    const p = new URLSearchParams();
    if (st.q) p.set('q', st.q);
    if (st.color) p.set('color', st.color);
    if (st.size) p.set('size', st.size);
    if (st.type) p.set('type', st.type);
    if (st.brand) p.set('brand', st.brand);
    if (st.stock) p.set('availability', 'skladem');
    if (st.pmin > 0) p.set('price_min', String(st.pmin));
    if (st.pmax < PRICE_MAX) p.set('price_max', String(st.pmax));
    const qs = p.toString();
    return qs ? `/?${qs}` : '/';
  };

  const commit = (st: typeof s = s) => router.push(buildUrl(st));

  const commitPatch = (patch: Partial<typeof s>) => {
    const next = { ...s, ...patch };
    setS(next);
    router.push(buildUrl(next));
  };

  const chips: { label: string; clear: () => typeof s }[] = [];
  if (s.q) chips.push({ label: `„${s.q}"`, clear: () => ({ ...s, q: '' }) });
  if (s.color) chips.push({ label: cap(s.color), clear: () => ({ ...s, color: '' }) });
  if (s.size) chips.push({ label: `Velikost ${s.size}`, clear: () => ({ ...s, size: '' }) });
  if (s.type) { 
    const t = TYPES.find(x => x[1] === s.type); 
    chips.push({ label: t ? t[0] : s.type, clear: () => ({ ...s, type: '' }) }); 
  }
  if (s.pmin > 0 || s.pmax < PRICE_MAX) chips.push({ label: `${s.pmin}–${s.pmax >= PRICE_MAX ? PRICE_MAX + '+' : s.pmax} Kč`, clear: () => ({ ...s, pmin: 0, pmax: PRICE_MAX }) });
  if (s.stock) chips.push({ label: 'Skladem', clear: () => ({ ...s, stock: false }) });
  if (s.brand) chips.push({ label: s.brand, clear: () => ({ ...s, brand: '' }) });

  const removeChip = (clear: () => typeof s) => { const next = clear(); setS(next); commit(next); };

  const onPrice = (which: 'pmin' | 'pmax', value: number) => {
    let { pmin, pmax } = s;
    if (which === 'pmin') pmin = Math.min(value, pmax - PRICE_STEP);
    else pmax = Math.max(value, pmin + PRICE_STEP);
    set({ pmin, pmax });
  };

  const fmtMax = (v: number) => (v >= PRICE_MAX ? `${PRICE_MAX}+` : v);

  return (
    <aside className="w-full lg:w-72 shrink-0 lg:sticky lg:top-[92px]">
      <div className="noir-panel overflow-hidden">
        {/* head */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--line)' }}>
          <span className="font-mono uppercase" style={{ fontSize: '11px', letterSpacing: '.18em', color: 'var(--gold)' }}>Filtr výběru</span>
          <span className={`flt-count ${chips.length === 0 ? 'zero' : ''}`}>{chips.length}</span>
        </div>

        <div className="px-5 pb-2">
          {/* active chips */}
          {chips.length > 0 && (
            <div className="flt-active">
              <div className="flt-achips">
                {chips.map((c, i) => (
                  <button key={i} type="button" className="flt-achip" onClick={() => removeChip(c.clear)}>
                    <b>{c.label}</b><span className="x">✕</span>
                  </button>
                ))}
              </div>
              <button type="button" className="flt-clear" onClick={() => { setS({ q: '', color: '', size: '', type: '', brand: '', stock: false, pmin: 0, pmax: PRICE_MAX }); router.push('/'); }}>
                Vymazat vše
              </button>
            </div>
          )}

          {/* Type of product (NEW) */}
          <div className="flt-block">
            <div className="flt-label">Typ produktu</div>
            <div className="flt-chips">
              {TYPES.map(([lbl, val]) => (
                <button key={val} type="button" className={`flt-chip ${s.type === val ? 'on' : ''}`} onClick={() => commitPatch({ type: s.type === val ? '' : val })}>{lbl}</button>
              ))}
            </div>
          </div>

          {/* search */}
          <div className="flt-block">
            <div className="flt-label">Hledání</div>
            <div className="flt-search">
              <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
              <input
                type="text"
                value={s.q}
                placeholder="např. luxusní, krajka…"
                onChange={e => set({ q: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') commit(); }}
              />
              {s.q && <button type="button" className="clr" title="Vymazat" onClick={() => commitPatch({ q: '' })}>✕</button>}
            </div>
          </div>

          {/* color */}
          <div className="flt-block">
            <div className="flt-label">Barva <span className="pick">{s.color ? cap(s.color) : ''}</span></div>
            <div className="flt-sw-grid">
              {COLORS.map(([name, hex, light]) => (
                <button
                  key={name}
                  type="button"
                  title={name}
                  data-light={light}
                  className={`flt-sw ${s.color === name ? 'on' : ''}`}
                  style={{ background: hex }}
                  onClick={() => commitPatch({ color: s.color === name ? '' : name })}
                />
              ))}
            </div>
          </div>

          {/* size */}
          <div className="flt-block">
            <div className="flt-label">Velikost</div>
            <div className="flt-pills">
              {SIZES.map(sz => (
                <button key={sz} type="button" className={`flt-pill ${s.size === sz ? 'on' : ''}`} onClick={() => commitPatch({ size: s.size === sz ? '' : sz })}>{sz}</button>
              ))}
            </div>
          </div>

          {/* price */}
          <div className="flt-block">
            <div className="flt-label">Cena</div>
            <div className="flt-range">
              <div className="rail" />
              <div className="fill" style={{ left: `${(s.pmin / PRICE_MAX) * 100}%`, right: `${100 - (s.pmax / PRICE_MAX) * 100}%` }} />
              <input type="range" min={0} max={PRICE_MAX} step={PRICE_STEP} value={s.pmin} onChange={e => onPrice('pmin', +e.target.value)} onMouseUp={() => commit()} onTouchEnd={() => commit()} onKeyUp={() => commit()} aria-label="Cena od" />
              <input type="range" min={0} max={PRICE_MAX} step={PRICE_STEP} value={s.pmax} onChange={e => onPrice('pmax', +e.target.value)} onMouseUp={() => commit()} onTouchEnd={() => commit()} onKeyUp={() => commit()} aria-label="Cena do" />
            </div>
            <div className="flt-price-vals">
              <span>od <b>{s.pmin}</b> Kč</span>
              <span>do <b>{fmtMax(s.pmax)}</b> Kč</span>
            </div>
            <div className="flt-presets">
              {[[0, 499, 'do 499'], [500, 999, '500–999'], [1000, PRICE_MAX, '1000+']].map(([mn, mx, lbl]) => (
                <button key={lbl} type="button" className="flt-preset" onClick={() => commitPatch({ pmin: mn as number, pmax: mx as number })}>{lbl}</button>
              ))}
            </div>
          </div>

          {/* availability */}
          <div className="flt-block">
            <div className="flt-switch-row" onClick={() => commitPatch({ stock: !s.stock })}>
              <span>Jen skladem</span>
              <div className={`flt-switch ${s.stock ? 'on' : ''}`} />
            </div>
          </div>

          {/* brand */}
          <div className="flt-block">
            <div className="flt-label">Značka</div>
            <select className="noir-field" value={s.brand} onChange={e => commitPatch({ brand: e.target.value })}>
              <option value="">Všechny značky</option>
              {brandsList.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>

        {/* footer CTA */}
        <div className="px-5 py-4 border-t" style={{ borderColor: 'var(--line)', background: 'rgba(11,11,13,.4)' }}>
          <button type="button" className="btn-gold w-full" onClick={() => commit()}>Zobrazit výsledky</button>
        </div>
      </div>
    </aside>
  );
}
